"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, setCurrentStation, type StationBrief } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

const STORAGE_KEY = "roa_station";

interface StationContextValue {
  // Stations this account may work on.
  stations: StationBrief[];
  // The one the dashboard is showing; null until the list has loaded (or when
  // the account has no stations yet).
  station: StationBrief | null;
  select: (id: number) => void;
  // Re-read the list (after creating or removing a station).
  reload: () => Promise<void>;
  loaded: boolean;
}

const StationContext = createContext<StationContextValue | undefined>(undefined);

function remembered(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) || null : null;
  } catch {
    return null;
  }
}

export function StationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [stations, setStations] = useState<StationBrief[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const apply = useCallback((list: StationBrief[], prefer: number | null) => {
    const pick = list.find((s) => s.id === prefer) ?? list[0] ?? null;
    // Set before the pages render so their first requests already carry it.
    setCurrentStation(pick ? pick.id : null, pick ? pick.mount : null);
    setStations(list);
    setSelected(pick ? pick.id : null);
    setLoaded(true);
  }, []);

  const reload = useCallback(async () => {
    try {
      const list = await api.myStations();
      apply(list, selected ?? remembered());
    } catch {
      // keep what we have
    }
  }, [apply, selected]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    api
      .myStations()
      .then((list) => {
        if (!cancelled) apply(list, remembered());
      })
      .catch(() => {
        // Still let the page render; calls fall back to the server's default station.
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, apply]);

  const select = useCallback(
    (id: number) => {
      if (!stations.some((s) => s.id === id)) return;
      try {
        localStorage.setItem(STORAGE_KEY, String(id));
      } catch {
        // not remembered; fine
      }
      setCurrentStation(id, stations.find((x) => x.id === id)?.mount ?? null);
      setSelected(id);
    },
    [stations]
  );

  const value = useMemo<StationContextValue>(
    () => ({ stations, station: stations.find((s) => s.id === selected) ?? null, select, reload, loaded }),
    [stations, selected, select, reload, loaded]
  );

  return <StationContext.Provider value={value}>{children}</StationContext.Provider>;
}

export function useStation() {
  const ctx = useContext(StationContext);
  if (!ctx) throw new Error("useStation must be used within StationProvider");
  return ctx;
}
